import type { EnvironmentId } from "@afar-research/contracts";
import { useNavigate } from "@tanstack/react-router";
import { HourglassIcon, WifiOffIcon } from "lucide-react";

import { reconnectCloudWorkspace } from "../connection/cloudWorkspaceRegistration";
import { useReconnectEnvironment } from "../hooks/useReconnectEnvironment";
import { cn } from "../lib/utils";
import { readLocalApi } from "../localApi";
import { useEnvironment } from "../state/environments";
import {
  presentWorkspaceShellUnavailable,
  type WorkspaceShellEntry,
} from "../state/workspaceShellEntry.logic";
import { EnvironmentRecoveryButton } from "./EnvironmentRecoveryButton";
import { CloudWorkspacesPanel, cloudBridge } from "./settings/CloudWorkspaces";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import {
  WorkspaceEmptyStateFrame,
  WorkspaceEmptyStateInset,
  WorkspaceEmptyStateMessage,
} from "./WorkspaceEmptyStateFrame";

/** Widths cycle rather than randomize, so the placeholder renders the same on every pass. */
const MESSAGE_GHOSTS = [
  { key: "first", align: "items-end", lines: ["w-48"] },
  { key: "second", align: "items-start", lines: ["w-4/5", "w-3/5", "w-2/3"] },
  { key: "third", align: "items-end", lines: ["w-64"] },
  { key: "fourth", align: "items-start", lines: ["w-3/4", "w-1/2"] },
] as const;

/**
 * What the workspace shows in place of every page until the shell can open: the
 * conversation's own shape while the first snapshot is on its way, and the way back in
 * when no Environment can deliver one.
 */
export function WorkspaceShellEntryState(props: {
  readonly entry: Exclude<WorkspaceShellEntry, { readonly kind: "ready" }>;
}) {
  return (
    <WorkspaceEmptyStateInset>
      {props.entry.kind === "synchronizing" ? (
        <WorkspaceShellSynchronizing />
      ) : props.entry.kind === "no-environment" ? (
        <WorkspaceShellNoEnvironment />
      ) : props.entry.environmentId === null ? (
        <WorkspaceShellPrimaryUnreachable />
      ) : (
        <WorkspaceShellUnavailable environmentId={props.entry.environmentId} />
      )}
    </WorkspaceEmptyStateInset>
  );
}

function WorkspaceShellNoEnvironment() {
  const navigate = useNavigate();
  const bridge = cloudBridge(readLocalApi()?.connect);
  return (
    <WorkspaceEmptyStateFrame heading="Cloud Environments" layout="timeline">
      <div className="mx-auto h-full max-w-3xl overflow-y-auto px-4 pb-8">
        <h1 className="mb-2 text-xl font-medium">Connect a Cloud Environment</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Connect one of your Cloud Environments to start working in AFAR. New ones are rented on
          the Network page, with every other machine you have.
        </p>
        {bridge ? (
          <CloudWorkspacesPanel
            bridge={bridge}
            offline={false}
            listConnectable
            onAdded={() => void navigate({ to: "/" })}
            onRent={() => void navigate({ to: "/settings/network", search: { add: "rent" } })}
          />
        ) : (
          <Button onClick={() => void navigate({ to: "/settings/connections" })}>
            Open connection settings
          </Button>
        )}
        <OwnMachinesToConnect />
      </div>
    </WorkspaceEmptyStateFrame>
  );
}

/**
 * The machines this device reached through the account's AFAR Network are as much a way in as a
 * Cloud Workspace (ADR-0146): each connects from here, and more are added in Settings → Network.
 */
function OwnMachinesToConnect() {
  const navigate = useNavigate();
  const networks = readLocalApi()?.connect?.networks;
  if (!networks) return null;
  const machines = networks.rememberedMachines();
  return (
    <section className="mt-8 space-y-3">
      <h2 className="text-sm font-medium">Your machines</h2>
      {machines.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Reach a Mac, Linux, or Windows machine you own through your AFAR Network.
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {machines.map((machine) => (
            <li key={machine.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="truncate text-sm">{machine.label}</span>
              <Button
                size="xs"
                onClick={() => reconnectCloudWorkspace({ id: machine.id, label: machine.label })}
              >
                Connect
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => void navigate({ to: "/settings/network" })}
      >
        Add a machine
      </Button>
    </section>
  );
}

function WorkspaceShellSynchronizing() {
  return (
    <WorkspaceEmptyStateFrame heading="Connecting..." layout="timeline">
      <div
        role="status"
        aria-label="Connecting to your Environment"
        className="mx-auto flex w-full max-w-3xl flex-col gap-8"
      >
        {MESSAGE_GHOSTS.map((message) => (
          <div key={message.key} className={cn("flex w-full flex-col gap-2", message.align)}>
            {message.lines.map((width) => (
              <Skeleton key={width} className={cn("h-3.5", width)} />
            ))}
          </div>
        ))}
      </div>
    </WorkspaceEmptyStateFrame>
  );
}

function WorkspaceShellUnavailable(props: { readonly environmentId: EnvironmentId }) {
  const navigate = useNavigate();
  const environment = useEnvironment(props.environmentId);
  const reconnect = useReconnectEnvironment();
  if (environment === null) return <WorkspaceShellSynchronizing />;
  const message = presentWorkspaceShellUnavailable({
    label: environment.label,
    connection: environment.connection,
  });
  const Icon = message.waiting ? HourglassIcon : WifiOffIcon;
  return (
    <WorkspaceEmptyStateFrame heading={environment.label}>
      <WorkspaceEmptyStateMessage
        icon={<Icon className="size-5 text-muted-foreground/60" aria-hidden />}
        title={message.title}
        detail={message.detail}
        action={
          <EnvironmentRecoveryButton
            connection={environment.connection}
            onRetry={() => void reconnect(props.environmentId)}
            onOpenConnectionSettings={() => void navigate({ to: "/settings/connections" })}
          />
        }
      />
    </WorkspaceEmptyStateFrame>
  );
}

/**
 * The primary Environment never answered, so there is no catalog entry to reconnect; looking
 * again means loading the app again, which asks the Environment Service from the start. AFAR
 * also looks again on its own, so this waits rather than calling the Environment out of reach.
 */
function WorkspaceShellPrimaryUnreachable() {
  return (
    <WorkspaceEmptyStateFrame heading="Environment Service">
      <WorkspaceEmptyStateMessage
        icon={<HourglassIcon className="size-5 text-muted-foreground/60" aria-hidden />}
        title="Waiting for the Environment Service"
        detail="The Environment Service for this app is not answering, so its projects and threads cannot be shown yet. AFAR looks again when the network returns or the computer wakes."
        action={<Button onClick={() => window.location.reload()}>Reload</Button>}
      />
    </WorkspaceEmptyStateFrame>
  );
}
